import { useState, useEffect, useRef } from "react";
import { Settings as SettingsIcon, Upload, X, Copy, ExternalLink, Globe, Trophy, Download, DatabaseBackup, CloudUpload, RefreshCw, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { getSettings, updateSettings } from "../lib/api";
import { toast } from "sonner";
import api from "../lib/api";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [logo, setLogo] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [productionUrl, setProductionUrl] = useState(() => {
    return localStorage.getItem("productionUrl") || "";
  });
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await getSettings();
      setSettings(res.data);
      setName(res.data.name || "GOODWILLERS INDOOR TOURNAMENT");
      setLogo(res.data.logo || "");
      setIsPublic(res.data.is_public || false);
      setAdminEmail(res.data.admin_email || "");
    } catch (error) {
      toast.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image must be less than 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (field, value) => {
    setSaving(true);
    try {
      const params = { [field]: value };
      const res = await updateSettings(params);
      setSettings(res.data);
      toast.success("Settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLogo = async () => {
    await handleSave("logo", logo);
  };

  const handleTogglePublic = async () => {
    const newValue = !isPublic;
    setIsPublic(newValue);
    await handleSave("is_public", newValue);
  };

  const publicUrl = settings?.public_token
    ? `${window.location.origin}/public/${settings.public_token}`
    : null;

  const copyPublicUrl = () => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      toast.success("URL copied to clipboard!");
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export-data");
      const dataStr = JSON.stringify(res.data, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      
      // Create and configure download link
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      link.setAttribute("download", `tournament_backup_${new Date().toISOString().split('T')[0]}.json`);
      
      // Append to body, click, then cleanup
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("Data exported! Check your downloads folder.");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const handleCopyExportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export-data");
      const dataStr = JSON.stringify(res.data, null, 2);
      await navigator.clipboard.writeText(dataStr);
      toast.success("Data copied to clipboard! Paste it into a .json file to save.");
    } catch (error) {
      console.error("Copy error:", error);
      toast.error("Failed to copy data");
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Confirm before importing
      if (!window.confirm("⚠️ This will REPLACE all existing data. Are you sure?")) {
        setImporting(false);
        return;
      }

      await api.post("/admin/import-data", data);
      toast.success("Data imported successfully! Refreshing...");
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      toast.error("Failed to import data. Please check the file format.");
    } finally {
      setImporting(false);
      if (importInputRef.current) {
        importInputRef.current.value = "";
      }
    }
  };

  const handleSaveProductionUrl = () => {
    localStorage.setItem("productionUrl", productionUrl);
    toast.success("Production URL saved!");
  };

  const handleSyncToProduction = async () => {
    if (!productionUrl) {
      toast.error("Please enter your production URL first");
      return;
    }

    // Validate URL format
    let url = productionUrl.trim();
    if (!url.startsWith("http")) {
      url = "https://" + url;
    }
    // Remove trailing slash
    url = url.replace(/\/$/, "");

    if (!window.confirm(`⚠️ This will sync ALL data to:\n${url}\n\nThis will REPLACE all data on the production server. Continue?`)) {
      return;
    }

    setSyncing(true);
    try {
      // Step 1: Export data from current environment
      toast.info("Step 1/3: Exporting data...");
      const exportRes = await api.get("/admin/export-data");
      const data = exportRes.data;

      // Step 2: Get auth token from production
      toast.info("Step 2/3: Connecting to production...");
      const loginRes = await fetch(`${url}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: "admin123" })
      });
      
      if (!loginRes.ok) {
        throw new Error("Failed to authenticate with production server. Check URL and credentials.");
      }
      
      const loginData = await loginRes.json();
      const prodToken = loginData.token;

      // Step 3: Import data to production
      toast.info("Step 3/3: Uploading to production...");
      const importRes = await fetch(`${url}/api/admin/import-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${prodToken}`
        },
        body: JSON.stringify(data)
      });

      if (!importRes.ok) {
        throw new Error("Failed to import data to production server.");
      }

      const result = await importRes.json();
      toast.success(`✅ Sync complete! Imported: ${result.imported.matches} matches, ${result.imported.teams} teams, ${result.imported.players} players`);
    } catch (error) {
      console.error("Sync error:", error);
      toast.error(error.message || "Failed to sync to production");
    } finally {
      setSyncing(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-8 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
          Settings
        </h1>
        <p className="text-muted-foreground mt-2">
          Tournament configuration and public sharing
        </p>
      </div>

      {/* Tournament Info */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <SettingsIcon className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider">
            Tournament Info
          </h2>
        </div>

        {/* Name */}
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Tournament Name
          </Label>
          <div className="flex gap-3">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-background border-border"
              data-testid="tournament-name-input"
            />
            <Button
              onClick={() => handleSave("name", name)}
              disabled={saving}
              variant="secondary"
              data-testid="save-name-btn"
            >
              Save
            </Button>
          </div>
        </div>

        {/* Logo */}
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Tournament Logo
          </Label>
          <div className="flex items-start gap-4">
            {logo ? (
              <div className="relative">
                <img
                  src={logo}
                  alt="Logo"
                  className="w-24 h-24 rounded-xl object-cover border border-border"
                />
                <button
                  onClick={() => setLogo("")}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                  data-testid="remove-logo-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 bg-secondary rounded-xl flex items-center justify-center border border-border">
                <Trophy className="w-10 h-10 text-muted-foreground/50" />
              </div>
            )}
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-tournament-logo-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Logo
              </Button>
              {logo && (
                <Button
                  onClick={handleSaveLogo}
                  disabled={saving}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="save-logo-btn"
                >
                  Save Logo
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            This logo will appear on all pages and the public view
          </p>
        </div>

        {/* Admin Email */}
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Admin Email (for contact form)
          </Label>
          <div className="flex gap-3">
            <Input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@example.com"
              className="bg-background border-border"
              data-testid="admin-email-input"
            />
            <Button
              onClick={() => handleSave("admin_email", adminEmail)}
              disabled={saving}
              variant="secondary"
              data-testid="save-email-btn"
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Public Sharing */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <Globe className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider">
            Public Sharing
          </h2>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Enable Public View</p>
            <p className="text-sm text-muted-foreground">
              Allow anyone with the link to view standings, matches, and stats
            </p>
          </div>
          <Switch
            checked={isPublic}
            onCheckedChange={handleTogglePublic}
            data-testid="public-toggle"
          />
        </div>

        {isPublic && publicUrl && (
          <div className="space-y-3 pt-4 border-t border-border">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Public URL
            </Label>
            <div className="flex gap-2">
              <Input
                value={publicUrl}
                readOnly
                className="bg-background border-border font-mono text-sm"
                data-testid="public-url-input"
              />
              <Button variant="secondary" onClick={copyPublicUrl} data-testid="copy-url-btn">
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.open(publicUrl, "_blank")}
                data-testid="open-url-btn"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Share this URL with players and fans to view the tournament
            </p>
          </div>
        )}
      </div>

      {/* Data Backup & Restore */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <DatabaseBackup className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider">
            Data Backup & Restore
          </h2>
        </div>

        <p className="text-sm text-muted-foreground">
          Export your tournament data to a JSON file for backup, or import data from a previous backup.
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Export */}
          <div className="p-4 bg-secondary/30 rounded-xl space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Download className="w-4 h-4 text-primary" />
              Export Data
            </h3>
            <p className="text-xs text-muted-foreground">
              Download all teams, players, matches, blog posts, sponsors, and settings.
            </p>
            <div className="space-y-2">
              <Button
                onClick={handleExportData}
                disabled={exporting}
                variant="secondary"
                className="w-full"
                data-testid="export-data-btn"
              >
                {exporting ? "Exporting..." : "Download Backup"}
              </Button>
              <Button
                onClick={handleCopyExportData}
                disabled={exporting}
                variant="outline"
                className="w-full text-xs"
                data-testid="copy-data-btn"
              >
                <Copy className="w-3 h-3 mr-2" />
                Copy to Clipboard
              </Button>
            </div>
          </div>

          {/* Import */}
          <div className="p-4 bg-secondary/30 rounded-xl space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Upload className="w-4 h-4 text-primary" />
              Import Data
            </h3>
            <p className="text-xs text-muted-foreground">
              Restore data from a backup file. This will replace all existing data.
            </p>
            <input
              ref={importInputRef}
              type="file"
              accept=".json"
              onChange={handleImportFile}
              className="hidden"
            />
            <Button
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              variant="secondary"
              className="w-full"
              data-testid="import-data-btn"
            >
              {importing ? "Importing..." : "Upload Backup"}
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground border-t border-border pt-4">
          💡 <strong>Tip:</strong> Export your data before deploying to a new environment, then import it there.
        </p>
      </div>

      {/* Sync to Production */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <CloudUpload className="w-5 h-5 text-green-500" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider">
            Sync to Production
          </h2>
        </div>

        <p className="text-sm text-muted-foreground">
          Push all data from this preview environment to your deployed production site with one click.
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="productionUrl" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Production URL
            </Label>
            <div className="flex gap-2">
              <Input
                id="productionUrl"
                value={productionUrl}
                onChange={(e) => setProductionUrl(e.target.value)}
                placeholder="goodwillersindoor.ca"
                className="bg-background border-border font-mono text-sm"
                data-testid="production-url-input"
              />
              <Button 
                variant="outline" 
                onClick={handleSaveProductionUrl}
                data-testid="save-production-url-btn"
              >
                Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Enter your deployed site URL (without https://)
            </p>
          </div>

          <Button
            onClick={handleSyncToProduction}
            disabled={syncing || !productionUrl}
            className="w-full bg-green-600 hover:bg-green-700"
            data-testid="sync-to-production-btn"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <CloudUpload className="w-4 h-4 mr-2" />
                Sync All Data to Production
              </>
            )}
          </Button>
        </div>

        <div className="text-xs text-muted-foreground border-t border-border pt-4 space-y-1">
          <p>⚠️ <strong>Warning:</strong> This will replace ALL data on the production server.</p>
          <p>✅ Syncs: Teams, Players, Matches, Standings, Blog, Sponsors, Settings</p>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <Lock className="w-5 h-5 text-orange-500" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider">
            Change Password
          </h2>
        </div>

        <p className="text-sm text-muted-foreground">
          Update your admin password. Make sure to remember your new password.
        </p>

        <div className="space-y-4">
          {/* Current Password */}
          <div className="space-y-2">
            <Label htmlFor="currentPassword" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Current Password
            </Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="bg-background border-border pr-10"
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              New Password
            </Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className="bg-background border-border pr-10"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Confirm New Password
            </Label>
            <Input
              id="confirmPassword"
              type={showNewPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="bg-background border-border"
              data-testid="confirm-password-input"
            />
          </div>

          <Button
            onClick={handleChangePassword}
            disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
            className="w-full bg-orange-600 hover:bg-orange-700"
            data-testid="change-password-btn"
          >
            {changingPassword ? "Changing..." : "Change Password"}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground border-t border-border pt-4">
          🔐 <strong>Tip:</strong> Use a strong password with at least 6 characters.
        </p>
      </div>
    </div>
  );
}
